import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ArticleCard as ArticleCardPayload, CategoryRef } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { queryKeys, STALE } from '@/lib/query';
import { ArticleCard } from '@/components/content';
import { CardGridSkeleton } from '@/routes/page-parts';
import { EmptyState, Tabs } from '@/components/ui';

/**
 * PRD §9's "Latest & Important", with the filter strip.
 *
 * The task list asks for filtering "client-side from the aggregate payload where
 * possible, refetching only when necessary", and that is exactly what happens: ALL is
 * already in the homepage payload, and a category tab filters it locally first. A fetch
 * only happens when the local slice is thin — the aggregate carries twelve mixed stories,
 * so a quiet category can easily have two of them, and showing two cards under a tab that
 * has forty stories behind it would misrepresent the section.
 */
const LOCAL_FILTER_MINIMUM = 4;

export function LatestSection({
  articles,
  filters,
}: {
  articles: ArticleCardPayload[];
  filters: CategoryRef[];
}) {
  const { locale, t } = useLocale();
  const [active, setActive] = useState('all');

  const localMatches = useMemo(
    () =>
      active === 'all' ? articles : articles.filter((article) => article.category.slug === active),
    [articles, active],
  );

  const needsFetch = active !== 'all' && localMatches.length < LOCAL_FILTER_MINIMUM;

  const { data: fetched, isFetching } = useQuery({
    queryKey: queryKeys.feed(locale, { category: active }),
    queryFn: () => publicApi.articles({ locale, category: active, limit: 8 }),
    enabled: needsFetch,
    staleTime: STALE.feed,
  });

  const shown = needsFetch ? (fetched?.items ?? []) : localMatches;

  return (
    <>
      <Tabs
        className="mb-5"
        label={t('section.latest')}
        value={active}
        onChange={setActive}
        items={[
          { id: 'all', label: t('filter.all') },
          ...filters.map((filter) => ({ id: filter.slug, label: filter.name })),
        ]}
      />

      {needsFetch && isFetching && shown.length === 0 ? (
        <CardGridSkeleton count={4} label={t('status.loading')} />
      ) : shown.length === 0 ? (
        <EmptyState title={t('empty.noStories')} />
      ) : (
        <div className="grid grid-cols-1 gap-4.5 sm:grid-cols-2 lg:grid-cols-4">
          {shown.slice(0, 8).map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </>
  );
}
